import { useCallback, useEffect, useRef, useState } from 'react';
import Intercom, { boot, hide, onHide, onShow, onUnreadCountChange, show, shutdown, update } from '@intercom/messenger-js-sdk';

import { useGetIntercomAuth } from './useGetIntercomAuth';

type IntercomMessengerType = {
  toggleWidget: () => void;
  isOpen: boolean;
  isReady: boolean;
  unreadCount: number;
};

/*
  - Hook using the Intercom Messenger SDK to customize the chat widget UI
  - Documentation here https://developers.intercom.com/installing-intercom/docs/intercom-messenger
  - The default Intercom launcher is shown after boot
*/
export const useIntercomMessenger = (
  userId: string | undefined,
  enabled: boolean
): IntercomMessengerType => {
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const intercomAppId = import.meta.env.VITE_INTERCOM_APP_ID;

  const { data: intercomAuth, error: intercomAuthError } = useGetIntercomAuth(userId, enabled);

  const bootedRef = useRef(false);
  const listenersRegisteredRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (bootedRef.current) return;

    if (!intercomAppId) {
      console.error('Intercom app_id is not configured. Please set VITE_INTERCOM_APP_ID environment variable.');
      setIsReady(false);
      return;
    }

    if (!userId) {
      return;
    }

    if (!intercomAuth && !intercomAuthError) {
      return;
    }

    if (intercomAuthError) {
      console.error('Failed to fetch Intercom auth token:', intercomAuthError);
      setIsReady(false);
      return;
    }

    if (!intercomAuth) {
      return;
    }

    try {
      Intercom({
        app_id: intercomAppId,
        user_id: userId,
        intercom_user_jwt: intercomAuth.intercomUserJWT,
      });

      // The npm SDK's Intercom() loads the widget script but doesn't
      // render the launcher. An explicit boot() + update() is required.
      boot({
        app_id: intercomAppId,
        user_id: userId,
        intercom_user_jwt: intercomAuth.intercomUserJWT,
      });
      update({ hide_default_launcher: true, alignment: 'right' });

      bootedRef.current = true;
      setIsReady(true);
    } catch (error) {
      console.error('Failed to initialize Intercom:', error);
      setIsReady(false);
    }
  }, [enabled, userId, intercomAppId, intercomAuth, intercomAuthError]);

  // Shutdown Intercom when user logs out or changes
  useEffect(() => {
    if (enabled && userId) return;

    if (!bootedRef.current) {
      setIsReady(false);
      setIsOpen(false);
      return;
    }

    try {
      shutdown();
    } catch (error) {
      console.error('Failed to shutdown Intercom:', error);
    }

    bootedRef.current = false;
    listenersRegisteredRef.current = false;
    setIsReady(false);
    setIsOpen(false);
  }, [enabled, userId]);

  useEffect(() => {
    if (!isReady || listenersRegisteredRef.current) return;

    try {
      onShow(() => setIsOpen(true));
      onHide(() => setIsOpen(false));
      onUnreadCountChange((count: number) => setUnreadCount(count));
      listenersRegisteredRef.current = true;
    } catch (error) {
      console.error('Failed to register Intercom event listeners:', error);
    }
  }, [isReady]);

  useEffect(() => {
    return () => {
      if (bootedRef.current) {
        try {
          shutdown();
        } catch (error) {
          console.error('Failed to shutdown Intercom:', error);
        }
        bootedRef.current = false;
        listenersRegisteredRef.current = false;
      }
    };
  }, []);

  const hideWidget = useCallback(() => {
    try {
      hide();
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to hide Intercom widget:', error);
    }
  }, []);

  const showWidget = useCallback(() => {
    try {
      show();
      setIsOpen(true);
    } catch (error) {
      console.error('Failed to show Intercom widget:', error);
    }
  }, []);

  const toggleWidget = useCallback(() => {
    if (isOpen) {
      hideWidget();
    } else {
      showWidget();
    }
  }, [hideWidget, isOpen, showWidget]);

  return {
    toggleWidget,
    isOpen,
    isReady,
    unreadCount,
  };
};
